import { motion, type Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { designColor, styledReset } from '@pkg/primitives';

import { ButtonIcon } from '../../Sections/Profile/Buttons';

export const DepOptionsButton = styled(ButtonIcon)`
    &&& {
        width: 40px;
        height: 40px;
        border-radius: 100px;
    }
`;

///
/// Popover

interface StyledPopoverProps {
    $alignRight?: boolean;
}

enum PopoverVariant {
    Hidden = 'hidden',
    Show = 'show',
}

const popoverVariants: Variants = {
    [PopoverVariant.Hidden]: {
        opacity: 0,
        translateY: 0,
    },
    [PopoverVariant.Show]: {
        opacity: 1,
        translateY: 10,
    },
};

const popoverWidth = 320;

export const StyledPopover = styled(motion.div).attrs({
    variants: popoverVariants,
    initial: PopoverVariant.Hidden,
    animate: PopoverVariant.Show,
    exit: PopoverVariant.Hidden,
})<StyledPopoverProps>`
    position: absolute;
    // TODO: Should relate to a source of truth for stacking context.
    z-index: 10;
    right: ${({ $alignRight }) => ($alignRight ? 0 : 'auto')};
`;

export const Interior = styled.div`
    padding: 20px;
    width: ${popoverWidth}px;
    border-radius: 10px;
    background-color: ${designColor.coal};
`;

///
/// Private Badge

export const Wrapper = styled.div`
    background: linear-gradient(86.45deg, #780eff -4.33%, #bf28ff 102.27%);
    padding: 1px;
    border-radius: 30px;
    overflow: hidden;
    height: 17px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const Inner = styled.div<{ $bgColor?: string }>`
    border-radius: 30px;
    background: ${({ $bgColor }) => $bgColor || '#090719'};
    display: flex;
    height: 100%;
`;

export const Text = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 30px;
    font-weight: 600;
    font-size: 9px;
    line-height: 9px;
    color: white;
    padding: 0 6px;
    text-transform: uppercase;
    background: linear-gradient(86.45deg, #780eff -4.33%, #bf28ff 102.27%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
`;

///
/// Settings Actions

interface StyledSettingsActionProps {
    $destructive?: boolean;
    $fullWidth?: boolean;
    $slim?: boolean;
}

const actionPaddingDesktop = 6;
const actionWrapperOffsetPx = `-${actionPaddingDesktop}px`;

const actionBase = css`
    display: block;
    position: relative;
    padding: var(--yat_settings-action-padding);
    width: var(--var_settings-action-width);
    text-align: var(--yat_settings-action-alignment);
    color: var(--yat_settings-action-color);
    background-color: var(--yat_settings-action-bg-color);
    border-radius: var(--yat_settings-action-radius);
    transition-property: background-color;
    transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
    transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};

    &:hover {
        background-color: ${({ theme }) => theme.shades.white['7']};
    }

    &:active {
        background-color: ${({ theme }) => theme.shades.white['4']};
    }

    ///
    /// Focus ring

    &::before {
        content: '';
        position: absolute;
        top: -4px;
        right: -4px;
        bottom: -4px;
        left: -4px;
        margin: auto;
        display: block;
        border: 2px solid ${({ theme }) => theme.colors.purple};
        border-radius: calc(var(--yat_settings-action-radius) + 3px);
        opacity: 0;
        transition-property: opacity;
        transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
        transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};
    }

    // Unforuntately, not all browsers treat ':focus'
    // the same for <a /> elements.
    &:focus-visible::before {
        opacity: 1;
    }
`;

export const StyledSettingsActionGroup = styled.div<{ $slim?: boolean }>`
    display: grid;
    gap: 4px;
    justify-items: ${({ $slim }) => ($slim ? 'start' : 'normal')};
    margin: ${({ $slim }) => ($slim ? actionWrapperOffsetPx : 0)};
`;

export const StyledSettingsAction = styled.div<StyledSettingsActionProps>`
    --yat_settings-action-padding: ${({ $slim }) => ($slim ? `${actionPaddingDesktop}px` : '16px')};
    --var_settings-action-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
    --yat_settings-action-alignment: ${({ $slim }) => ($slim ? 'left' : 'center')};
    --yat_settings-action-font-size: ${({ $slim }) => ($slim ? '16px' : '18px')};
    --yat_settings-action-color: ${(props) => (props.$destructive ? props.theme.colors.red : props.theme.colors.white)};
    --yat_settings-action-bg-color: transparent;
    --yat_settings-action-radius: ${({ $slim }) => ($slim ? '4px' : '64px')};
`;

export const ButtonAction = styled.button.attrs({ type: 'button' })`
    && {
        ${styledReset.button}
        ${actionBase}
    }

    &&:disabled {
        cursor: not-allowed;
        color: ${(props) => props.theme.colors.grey};
    }
`;

export const LinkAction = styled(Link)`
    && {
        ${styledReset.link}
        ${actionBase}
    }
`;

export const Content = styled.span`
    ${styledReset.typography}
    display: block;
    font-family: 'Alliance No.1';
    font-size: var(--yat_settings-action-font-size);
    font-weight: 700;
    line-height: 1;
    color: currentColor;
`;
